import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Sparkles, Wand2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatBRL } from "../data/menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAMPLES = [
  "Doce com muito chocolate",
  "Refrescante com frutas",
  "Fitness com whey",
  "Cremoso de leite em pó",
];

export const AISuggester = ({ onAdd }) => {
  const [craving, setCraving] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const suggest = async (text) => {
    const value = (text ?? craving).trim();
    if (value.length < 3) {
      toast.error("Conte o que você está com vontade de comer");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/suggest-cup`, { craving: value });
      setResult(data);
    } catch (e) {
      toast.error("A IA não conseguiu sugerir agora. Tente de novo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7 }}
      className="mb-20 overflow-hidden rounded-[2rem] border border-amber-400/30 bg-[#2D0B5A]/70 backdrop-blur-xl"
      data-testid="ai-suggester"
    >
      <div className="grid lg:grid-cols-[1fr_1fr]">
        <div className="p-8 sm:p-10">
          <p className="flex items-center gap-2 font-grotesk text-xs font-bold uppercase tracking-[0.3em] text-amber-400">
            <Sparkles className="h-4 w-4" /> Monte com IA
          </p>
          <h3 className="mt-4 font-syne text-2xl font-extrabold text-white sm:text-3xl">
            Sem ideia? Conte sua vontade.
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-purple-200/80">
            Escreva o que você quer sentir — "algo doce com chocolate", "leve e
            refrescante" — e nossa IA monta o copo perfeito com itens do cardápio.
          </p>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <input
              data-testid="ai-craving-input"
              value={craving}
              onChange={(e) => setCraving(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && suggest()}
              placeholder="Ex: quero algo doce com Nutella e morango"
              maxLength={300}
              className="flex-1 rounded-full border border-purple-500/30 bg-white/5 px-6 py-4 text-sm text-white placeholder:text-purple-300/40 outline-none transition-colors duration-200 focus:border-amber-400"
            />
            <button
              type="button"
              data-testid="ai-suggest-button"
              onClick={() => suggest()}
              disabled={loading}
              className="flex items-center justify-center gap-2.5 rounded-full bg-amber-400 px-8 py-4 font-grotesk text-sm font-bold uppercase tracking-[0.12em] text-[#1E053B] transition-transform duration-300 enabled:hover:scale-[1.04] enabled:active:scale-95 disabled:opacity-60"
            >
              {loading ? (
                <Loader2 className="h-4.5 w-4.5 animate-spin" />
              ) : (
                <Wand2 className="h-4.5 w-4.5" />
              )}
              {loading ? "Montando..." : "Sugerir copo"}
            </button>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {EXAMPLES.map((ex, i) => (
              <button
                key={ex}
                type="button"
                data-testid={`ai-example-${i}`}
                onClick={() => {
                  setCraving(ex);
                  suggest(ex);
                }}
                disabled={loading}
                className="rounded-full border border-purple-500/25 bg-white/5 px-4 py-2 text-xs font-semibold text-purple-200/80 transition-colors duration-200 hover:border-amber-400 hover:text-amber-300 disabled:opacity-50"
              >
                {ex}
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-amber-400/20 bg-[#1E053B]/50 p-8 sm:p-10 lg:border-l lg:border-t-0">
          <AnimatePresence mode="wait">
            {result ? (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5 }}
                data-testid="ai-result"
              >
                <p className="font-grotesk text-[11px] font-bold uppercase tracking-[0.25em] text-emerald-400">
                  Sugestão da IA
                </p>
                <h4 className="mt-3 font-syne text-xl font-extrabold text-white">
                  Copo {result.size.label} · {result.flavor.label}
                </h4>
                <div className="mt-4 flex flex-wrap gap-2">
                  {result.addons.map((a) => (
                    <span
                      key={a.name}
                      className="rounded-full border border-purple-400/30 bg-white/5 px-3.5 py-1.5 text-xs font-semibold text-purple-100"
                    >
                      {a.name} · <span className="text-emerald-400">{formatBRL(a.price)}</span>
                    </span>
                  ))}
                </div>
                <p
                  data-testid="ai-reason"
                  className="mt-5 border-l-2 border-amber-400 pl-4 text-sm italic leading-relaxed text-purple-200/85"
                >
                  "{result.reason}"
                </p>
                <div className="mt-6 flex items-center justify-between gap-4">
                  <span
                    data-testid="ai-total"
                    className="font-syne text-3xl font-extrabold text-emerald-400"
                  >
                    {formatBRL(result.total)}
                  </span>
                  <button
                    type="button"
                    data-testid="ai-add-button"
                    onClick={() =>
                      onAdd({
                        title: `Copo ${result.size.label} · ${result.flavor.label} (sugestão IA)`,
                        details: result.addons.map((a) => a.name),
                        unitPrice: result.total,
                        qty: 1,
                      })
                    }
                    className="rounded-full bg-emerald-400 px-7 py-3.5 font-grotesk text-xs font-bold uppercase tracking-[0.12em] text-[#1E053B] transition-transform duration-300 hover:scale-[1.04] active:scale-95"
                  >
                    Adicionar ao pedido
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="placeholder"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex h-full min-h-[220px] flex-col items-center justify-center gap-4 text-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-9 w-9 animate-spin text-amber-400" />
                    <p className="text-sm text-purple-200/70">
                      A IA está escolhendo os melhores adicionais...
                    </p>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-9 w-9 text-purple-400/60" />
                    <p className="max-w-xs text-sm text-purple-200/60">
                      Sua sugestão personalizada aparece aqui, pronta para ir ao
                      pedido com um toque.
                    </p>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};
